import React, { useEffect, useState, useRef } from "react";
import { safeUrl } from "../utils/safeUrl";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { FaYoutube, FaInstagram, FaTiktok } from "react-icons/fa";
import { FiShield, FiEye, FiX, FiSearch, FiExternalLink, FiSend, FiCopy } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import { useStickyState } from "../hooks/useStickyState";
import { useScrollRestoration } from "../hooks/useScrollRestoration";
import { extractUsername, openProfilePopup } from "./components/ApplicationShared";

const getUserId = (row) => row?.userId ?? row?.user_id ?? row?.userID ?? null;

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const STATUS_FILTERS = [
  { label: "All",                value: "" },
  { label: "Pending",            value: "inProgress" },
  { label: "Under Verification", value: "underVerification" },
  { label: "Approved",           value: "approved" },
  { label: "Rejected",           value: "rejected" },
];

const PLATFORMS = [
  {
    key: "youtube",   label: "YouTube",   Icon: FaYoutube,   iconColor: "text-red-500",
    statusKey: "youtubeStatus",     urlKey: "youtubeProfileUrl",   codeKey: "youtubeCode",
    emailKey: "youtubeEmail",       codeSentKey: "isYoutubeCodeSend",
    codeVerifiedKey: "isYoutubeCodeVerified", codeSendDateKey: "youtubeCodeSendDate",
    codeVerifDateKey: "youtubeCodeVerificationDate", sendParam: "YouTube",
  },
  {
    key: "instagram", label: "Instagram", Icon: FaInstagram, iconColor: "text-pink-500",
    statusKey: "instagramStatus",   urlKey: "instagramProfileUrl", codeKey: "instagramCode",
    emailKey: null,                 codeSentKey: "isInstagramCodeSend",
    codeVerifiedKey: "isInstagramCodeVerified", codeSendDateKey: "instagramCodeSendDate",
    codeVerifDateKey: "instagramCodeVerificationDate", sendParam: "Instagram",
  },
  {
    key: "tiktok",   label: "TikTok",   Icon: FaTiktok,   iconColor: "text-gray-900",
    statusKey: "tiktokStatus",      urlKey: "tiktokProfileUrl",    codeKey: "tiktokCode",
    emailKey: null,                 codeSentKey: "isTiktokCodeSend",
    codeVerifiedKey: "isTiktokCodeVerified",   codeSendDateKey: "tiktokCodeSendDate",
    codeVerifDateKey: "tiktokCodeVerificationDate", sendParam: "TikTok",
  },
];

const STATUS_CONFIG = {
  inProgress:        { label: "In Progress",        cls: "bg-amber-50 text-amber-600" },
  underVerification: { label: "Under Verification",  cls: "bg-blue-50 text-blue-600" },
  approved:          { label: "Approved",            cls: "bg-green-50 text-green-700" },
  rejected:          { label: "Rejected",            cls: "bg-red-50 text-red-600" },
};

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-gray-300 text-xs">—</span>;
  const cfg = STATUS_CONFIG[status] || { label: status, cls: "bg-gray-100 text-gray-600" };
  return <span className={`inline-block text-xs font-medium px-2.5 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>;
};

const Spinner = ({ sm }) => (
  <div className={`border-2 border-current border-t-transparent rounded-full animate-spin ${sm ? "w-3.5 h-3.5" : "w-4 h-4"}`} />
);

export default function ProfileVerificationsPage() {
  const navigate = useNavigate();
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading]             = useState(false);
  const [pagination, setPagination]       = useState({});
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showModal, setShowModal]         = useState(false);
  const [statusFilter, setStatusFilter]   = useState("");
  const [searchQuery, setSearchQuery]     = useStickyState("", "pv_searchQuery");
  const [currentPage, setCurrentPage]     = useStickyState(1, "pv_currentPage");
  const [sendingPlatform, setSendingPlatform] = useState(null);
  const [rejectAllConfirm, setRejectAllConfirm] = useState(false);
  const [expandedId, setExpandedId]       = useStickyState(null, "pv_expandedId");
  const [expandedDetail, setExpandedDetail] = useStickyState(null, "pv_expandedDetail");
  const [expandedLoading, setExpandedLoading] = useState(false);
  const debounceTimer = useRef(null);

  const fetchVerifications = async (page = 1, status = "", search = "") => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const fetchLimit = search ? 10000 : 50;
      let url = `${baseUrl}/profile-verification-link/list?page=${search ? 1 : page}&limit=${fetchLimit}`;
      if (status) url += `&status=${status}`;
      // Fallback: we won't send search to backend if it's broken, we will filter locally instead
      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      if (data.status) {
        let items = data.data;
        
        if (search) {
          const s = search.toLowerCase();
          items = items.filter(detail => {
            const uName = String(detail.username || detail.user_username || detail.userName || '').toLowerCase();
            const uEmail = String(detail.email || detail.userEmail || detail.user_email || '').toLowerCase();
            return uName.includes(s) || uEmail.includes(s);
          });
        }

        if (status) {
          const f = status.toLowerCase();
          items = items.filter(detail => {
            const submitted = PLATFORMS.filter(p => Boolean(detail[p.urlKey]));
            if (submitted.length === 0) return false;
            
            const allApproved = submitted.every(p => String(detail[p.statusKey]).toLowerCase() === 'approved');
            const allRejected = submitted.every(p => String(detail[p.statusKey]).toLowerCase() === 'rejected');
            
            if (f === 'approved') return allApproved;
            if (f === 'rejected') return allRejected;
            if (f === 'inprogress' || f === 'underverification') return !allApproved && !allRejected;
            return true;
          });
        }
        setVerifications(items);
        setPagination(data.pagination);
      }
    } catch {
      toast.error("Failed to fetch verifications");
    }
    setLoading(false);
  };

  const fetchDetailData = async (id) => {
    const token = localStorage.getItem("token");
    const { data } = await axios.get(
      `${baseUrl}/profile-verification-link/details/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!data.status) throw new Error(data.message || "Failed to load details");
    return data.data || {};
  };

  const fetchDetail = async (id) => {
    setDetailLoading(true);
    try {
      const detail = await fetchDetailData(id);
      setSelectedDetail(detail);
      setShowModal(true);
      setRejectAllConfirm(false);
    } catch (err) {
      toast.error(err.message || "Failed to load details");
    }
    setDetailLoading(false);
  };

  const toggleExpand = async (item) => {
    if (expandedId === item.id) { setExpandedId(null); return; }
    setExpandedId(item.id);
    setExpandedDetail(null);
    setExpandedLoading(true);
    try {
      const detail = await fetchDetailData(item.id);
      setExpandedDetail(detail);
    } catch (err) {
      toast.error(err.message || "Failed to load details");
    }
    setExpandedLoading(false);
  };

  const patchRowInList = (id, patch) => {
    setVerifications(prev => prev.map(v => (v.id === id ? { ...v, ...patch } : v)));
  };

  const removeRowFromList = (id) => {
    setVerifications(prev => prev.filter(v => v.id !== id));
  };

  // A record is "fully rejected" once every platform that actually has a submitted
  // profile URL is rejected. Platforms never submitted (no profile URL) don't count.
  const isFullyRejected = (detail) => {
    const submitted = PLATFORMS.filter(p => Boolean(detail[p.urlKey]));
    if (submitted.length === 0) return false;
    return submitted.every(p => detail[p.statusKey] === "rejected");
  };

  // A record's codes are "fully sent" once every platform that has a verification
  // code to send has had it sent. Platforms with no code to send don't count.
  const isFullyCodeSent = (detail) => {
    const requiringCode = PLATFORMS.filter(p => detail[p.urlKey] && detail[p.codeKey] != null);
    if (requiringCode.length === 0) return false;
    return requiringCode.every(p => detail[p.codeSentKey] === 1);
  };

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchVerifications(currentPage, statusFilter, searchQuery);
    }, 500);
    return () => clearTimeout(debounceTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, currentPage]);

  useScrollRestoration("profile_verifications", !loading && verifications.length > 0);

  const copyVerificationCode = (code) => {
    navigator.clipboard.writeText(`Creator Hub Verification Code: ${code}`);
    toast.success("Verification code copied");
  };

  const formatDateTime = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return date.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const sendCode = async (id, platform) => {
    setSendingPlatform(platform);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.put(
        `${baseUrl}/profile-verification-link/send-code/${id}`,
        { type: platform },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      if (data.status) {
        toast.success(`${platform} code sent successfully`);
        const detail = await fetchDetailData(id);

        if (isFullyCodeSent(detail)) {
          toast("All codes sent — removed from list", { icon: "🗑️" });
          removeRowFromList(id);
          if (selectedDetail?.id === id) { setShowModal(false); setSelectedDetail(null); }
          if (expandedId === id) setExpandedId(null);
          return;
        }

        if (selectedDetail?.id === id) setSelectedDetail(detail);
        if (expandedId === id) setExpandedDetail(detail);
        patchRowInList(id, detail);
      } else {
        toast.error(data.message || `Failed to send ${platform} code`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Error sending ${platform} code`);
    } finally {
      setSendingPlatform(null);
    }
  };

  const handleUpdateStatus = async (id, status, type) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${baseUrl}/profile-verification-link/status/${id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ status, type }),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || "Failed to update status");
      toast.success(`${type} status updated to ${status}`);
      const detail = await fetchDetailData(id);

      if (status === "rejected" && isFullyRejected(detail)) {
        toast("All platforms rejected — removed from list", { icon: "🗑️" });
        removeRowFromList(id);
        if (selectedDetail?.id === id) { setShowModal(false); setSelectedDetail(null); setRejectAllConfirm(false); }
        if (expandedId === id) setExpandedId(null);
        return;
      }

      if (selectedDetail?.id === id) { setRejectAllConfirm(false); setSelectedDetail(detail); }
      if (expandedId === id) setExpandedDetail(detail);
      patchRowInList(id, detail);
    } catch (err) {
      toast.error(err.message || "Error updating status");
    }
  };

  const totalPages = pagination.totalPages || pagination.lastPage || 1;

  const getPaginationNumbers = (current, total) => {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 3) return [1, 2, 3, 4, "...", total];
    if (current >= total - 2) return [1, "...", total - 3, total - 2, total - 1, total];
    return [1, "...", current - 1, current, current + 1, "...", total];
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">

          {/* Heading */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
              <FiShield size={20} className="text-teal-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Creator Profile</h2>
              <p className="text-sm text-gray-400 mt-0.5">Manage creator profile verifications</p>
            </div>
            {!loading && pagination.total > 0 && (
              <span className="ml-auto text-xs bg-gray-200 text-gray-600 font-semibold px-2.5 py-1 rounded-full">
                {pagination.total} total
              </span>
            )}
          </div>

          {/* Main card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">

            {/* Search + Filter row */}
            <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
              <div className="relative">
                <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search email or profile..."
                  className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {STATUS_FILTERS.map(f => (
                  <button
                    key={f.value}
                    onClick={() => { setStatusFilter(f.value); setSearchQuery(""); }}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition ${
                      statusFilter === f.value
                        ? "bg-blue-600 text-white shadow-sm"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Table */}
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : verifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <FiShield size={32} className="text-gray-300 mb-3" />
                <p className="text-sm">No verifications found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3">#</th>
                      <th className="px-5 py-3">Email</th>
                      {PLATFORMS.map(p => (
                        <th key={p.key} className="px-5 py-3 text-center">{p.label}</th>
                      ))}
                      <th className="px-5 py-3 hidden md:table-cell">Created</th>
                      <th className="px-5 py-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {verifications.map((item, index) => (
                      <React.Fragment key={item.id ?? index}>
                        <tr
                          onClick={() => toggleExpand(item)}
                          className={`hover:bg-gray-50 transition cursor-pointer ${expandedId === item.id ? "bg-blue-50/40" : ""}`}
                        >
                          <td className="px-5 py-3.5 text-gray-400 text-xs">{index + 1}</td>
                          <td className="px-5 py-3.5 font-medium text-gray-800">
                            {getUserId(item) ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/user-full-details/${getUserId(item)}`, { state: { from: '/profile-verifications' } }); }}
                                className="hover:text-blue-600 hover:underline transition"
                                title="View user details"
                              >
                                {item.email || "—"}
                              </button>
                            ) : (
                              item.email || "—"
                            )}
                          </td>
                          {PLATFORMS.map(p => (
                            <td key={p.key} className="px-5 py-3.5 text-center">
                              {item[p.urlKey] ? (
                                <div className="flex flex-col items-center gap-1">
                                  <a href={safeUrl(item[p.urlKey])} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:opacity-70 transition">
                                    <p.Icon size={18} className={p.iconColor} />
                                  </a>
                                  {item[p.codeVerifiedKey] === 1 && (
                                    <span className="text-xs font-medium bg-green-50 text-green-700 px-2 py-0.5 rounded-full">Verified</span>
                                  )}
                                  {item[p.codeKey] && (
                                    <span className="text-xs font-mono text-gray-400">{item[p.codeKey]}</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-300 text-xs">—</span>
                              )}
                            </td>
                          ))}
                          <td className="px-5 py-3.5 hidden md:table-cell text-xs text-gray-400">
                            {formatDateTime(item.createdAt)}
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <button
                              onClick={(e) => { e.stopPropagation(); fetchDetail(item.id); }}
                              title="View Details"
                              className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition mx-auto block"
                            >
                              <FiEye size={14} />
                            </button>
                          </td>
                        </tr>

                        {/* Expanded row: Platform Verifications */}
                        {expandedId === item.id && (
                          <tr>
                            <td colSpan={4 + PLATFORMS.length} className="px-5 py-5 bg-gray-50/60 border-t border-gray-100">
                              {expandedLoading ? (
                                <div className="flex justify-center py-8">
                                  <div className="w-6 h-6 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
                                </div>
                              ) : expandedDetail ? (
                                <div>
                                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                    Platform Verifications
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {PLATFORMS.map(p => {
                                      const platformStatus = expandedDetail[p.statusKey];
                                      const profileUrl     = expandedDetail[p.urlKey];
                                      const code           = expandedDetail[p.codeKey];
                                      const codeSent        = expandedDetail[p.codeSentKey];
                                      const codeVerified    = expandedDetail[p.codeVerifiedKey];
                                      const codeSendDate    = expandedDetail[p.codeSendDateKey];
                                      const codeVerifDate   = expandedDetail[p.codeVerifDateKey];
                                      const email           = p.emailKey ? expandedDetail[p.emailKey] : null;
                                      const isInProgress     = !platformStatus || platformStatus === "inProgress";
                                      const isUnderVerif     = platformStatus === "underVerification";

                                      return (
                                        <div key={p.key} className="bg-white border border-gray-100 rounded-xl p-4 space-y-2.5">
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <p.Icon size={18} className={p.iconColor} />
                                              <span className="font-semibold text-sm text-gray-800">{p.label}</span>
                                            </div>
                                            <StatusBadge status={platformStatus} />
                                          </div>

                                          {profileUrl ? (
                                            <div className="space-y-2.5 text-xs text-gray-600">
                                              <div>
                                                <div className="flex items-center justify-between mb-0.5">
                                                  <div className="text-gray-400 font-medium uppercase tracking-wide">Profile URL</div>
                                                  <button 
                                                    onClick={(e) => { e.stopPropagation(); openProfilePopup(profileUrl); }} 
                                                    className="text-[11px] font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 px-2 py-0.5 rounded transition flex items-center gap-1"
                                                  >
                                                    View Profile <FiExternalLink size={10} />
                                                  </button>
                                                </div>
                                                <div className="text-gray-700 break-all mb-2.5">{profileUrl}</div>
                                                <div className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Username</div>
                                                <div className="text-gray-700 break-all">{extractUsername(profileUrl) ? `@${extractUsername(profileUrl)}` : 'Not Available'}</div>
                                              </div>
                                              {email && (
                                                <div>
                                                  <div className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Email</div>
                                                  <div className="text-gray-700">{email}</div>
                                                </div>
                                              )}
                                              <div>
                                                <div className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Verification Code</div>
                                                <div className="flex items-center gap-1.5">
                                                  <span className="font-mono text-gray-800">{code || "—"}</span>
                                                  {code && (
                                                    <button
                                                      onClick={(e) => { e.stopPropagation(); copyVerificationCode(code); }}
                                                      title="Copy verification code"
                                                      className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition"
                                                    >
                                                      <FiCopy size={12} />
                                                    </button>
                                                  )}
                                                </div>
                                              </div>
                                              <div>
                                                <div className="text-gray-400 font-medium uppercase tracking-wide mb-1">Code Sent</div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                  {codeSent === 1 ? (
                                                    <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">Sent</span>
                                                  ) : code == null ? (
                                                    <span className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Not Sent</span>
                                                  ) : null}
                                                  {code != null && (
                                                    <button
                                                      onClick={(e) => { e.stopPropagation(); sendCode(expandedDetail.id, p.sendParam); }}
                                                      disabled={sendingPlatform === p.sendParam}
                                                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition"
                                                    >
                                                      {sendingPlatform === p.sendParam ? <Spinner sm /> : <FiSend size={10} />}
                                                      {sendingPlatform === p.sendParam ? "Sending..." : "Send Code"}
                                                    </button>
                                                  )}
                                                </div>
                                                {codeSendDate && (
                                                  <div className="text-gray-400 mt-1">{formatDateTime(codeSendDate)}</div>
                                                )}
                                              </div>
                                              <div>
                                                <div className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Code Verified</div>
                                                {codeVerified === 1 ? (
                                                  <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">Verified</span>
                                                ) : (
                                                  <span className="bg-red-50 text-red-600 px-2 py-0.5 rounded-full">Not Verified</span>
                                                )}
                                                {codeVerifDate && (
                                                  <div className="text-gray-400 mt-1">{formatDateTime(codeVerifDate)}</div>
                                                )}
                                              </div>

                                              {(isInProgress || isUnderVerif) && (
                                                <div className="flex gap-2 pt-2 border-t border-gray-100 flex-wrap">
                                                  {isInProgress && (
                                                    <button
                                                      onClick={(e) => { e.stopPropagation(); handleUpdateStatus(expandedDetail.id, "underVerification", p.sendParam); }}
                                                      className="flex-1 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                                                    >
                                                      Under Verification
                                                    </button>
                                                  )}
                                                  <button
                                                    onClick={(e) => { e.stopPropagation(); handleUpdateStatus(expandedDetail.id, "rejected", p.sendParam); }}
                                                    className="flex-1 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition"
                                                  >
                                                    Reject
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            <p className="text-xs text-gray-400 italic">No {p.label} details available</p>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-400">No details available.</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && verifications.length > 0 && pagination && (
              <div className="px-5 py-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
                <span className="text-xs text-gray-400">
                  Page {pagination.currentPage} of {totalPages}
                  {pagination.total ? ` — ${pagination.total} total` : ""}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => {
                      const newPage = pagination.currentPage - 1;
                      setCurrentPage(newPage);
                      fetchVerifications(newPage, statusFilter, searchQuery);
                    }}
                    disabled={pagination.currentPage <= 1}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    Previous
                  </button>
                  {getPaginationNumbers(pagination.currentPage, totalPages).map((page, idx) =>
                    page === "..." ? (
                      <span key={idx} className="px-2 text-xs text-gray-400">…</span>
                    ) : (
                      <button
                        key={idx}
                        onClick={() => {
                          setCurrentPage(page);
                          fetchVerifications(page, statusFilter, searchQuery);
                        }}
                        className={`w-8 h-8 text-xs font-medium rounded-lg transition ${
                          pagination.currentPage === page
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => {
                      const newPage = pagination.nextPage;
                      setCurrentPage(newPage);
                      fetchVerifications(newPage, statusFilter, searchQuery);
                    }}
                    disabled={!pagination.nextPage}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-5xl flex flex-col max-h-[90vh]"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-base font-semibold text-gray-800">Profile Verification Details</h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                <FiX size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 p-6 space-y-6">
              {detailLoading ? (
                <div className="flex justify-center py-12">
                  <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : selectedDetail ? (
                <>
                  {/* General Info */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">General Information</div>
                      {rejectAllConfirm ? (
                        <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                          <span className="text-xs text-red-600 font-medium">Reject all platforms?</span>
                          <button
                            onClick={() => handleUpdateStatus(selectedDetail.id, "rejected", "all")}
                            className="px-2.5 py-1 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setRejectAllConfirm(false)}
                            className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setRejectAllConfirm(true)}
                          className="flex items-center gap-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-100 px-3 py-1.5 rounded-lg transition"
                        >
                          <FiX size={12} /> Reject All Platforms
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                      {Object.entries(selectedDetail)
                        .filter(([key]) =>
                          !key.toLowerCase().includes("youtube") &&
                          !key.toLowerCase().includes("tiktok") &&
                          !key.toLowerCase().includes("instagram")
                        )
                        .map(([key, value]) => (
                          <div key={key}>
                            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">
                              {key.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase())}
                            </div>
                            <div className="text-sm text-gray-800 break-words">
                              {value === null || value === undefined || value === ""
                                ? <span className="text-gray-300">—</span>
                                : String(value)}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Platform Cards */}
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 pt-2 border-t border-gray-100">
                      Platform Verifications
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {PLATFORMS.map(p => {
                        const platformStatus = selectedDetail[p.statusKey];
                        const profileUrl     = selectedDetail[p.urlKey];
                        const code           = selectedDetail[p.codeKey];
                        const codeSent       = selectedDetail[p.codeSentKey];
                        const codeVerified   = selectedDetail[p.codeVerifiedKey];
                        const codeSendDate   = selectedDetail[p.codeSendDateKey];
                        const codeVerifDate  = selectedDetail[p.codeVerifDateKey];
                        const email          = p.emailKey ? selectedDetail[p.emailKey] : null;
                        const isInProgress   = !platformStatus || platformStatus === "inProgress";
                        const isUnderVerif   = platformStatus === "underVerification";

                        return (
                          <div key={p.key} className="border border-gray-100 rounded-xl p-4 space-y-3">
                            {/* Card header */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <p.Icon size={18} className={p.iconColor} />
                                <span className="font-semibold text-sm text-gray-800">{p.label}</span>
                              </div>
                              <StatusBadge status={platformStatus} />
                            </div>

                            {profileUrl ? (
                              <div className="space-y-2.5 text-xs text-gray-600">

                                {/* Profile URL */}
                                <div>
                                  <div className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Profile URL</div>
                                  <a
                                    href={safeUrl(profileUrl)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-600 hover:underline flex items-center gap-1 break-all"
                                  >
                                    {profileUrl} <FiExternalLink size={10} />
                                  </a>
                                </div>

                                {/* Email (YouTube only) */}
                                {email && (
                                  <div>
                                    <div className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Email</div>
                                    <div className="text-gray-700">{email}</div>
                                  </div>
                                )}

                                {/* Verification code */}
                                <div>
                                  <div className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Verification Code</div>
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-mono text-gray-800">{code || "—"}</span>
                                    {code && (
                                      <button
                                        onClick={() => copyVerificationCode(code)}
                                        title="Copy verification code"
                                        className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition"
                                      >
                                        <FiCopy size={12} />
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* Code sent status + Send button */}
                                <div>
                                  <div className="text-gray-400 font-medium uppercase tracking-wide mb-1">Code Sent</div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {codeSent === 1 ? (
                                      <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">Sent</span>
                                    ) : code === null ? (
                                      <span className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Not Sent</span>
                                    ) : null}
                                    {code !== null && (
                                      <button
                                        onClick={() => sendCode(selectedDetail.id, p.sendParam)}
                                        disabled={sendingPlatform === p.sendParam}
                                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition"
                                      >
                                        {sendingPlatform === p.sendParam ? <Spinner sm /> : <FiSend size={10} />}
                                        {sendingPlatform === p.sendParam ? "Sending..." : "Send Code"}
                                      </button>
                                    )}
                                  </div>
                                  {codeSendDate && (
                                    <div className="text-gray-400 mt-1">{formatDateTime(codeSendDate)}</div>
                                  )}
                                </div>

                                {/* Code verified */}
                                <div>
                                  <div className="text-gray-400 font-medium uppercase tracking-wide mb-0.5">Code Verified</div>
                                  {codeVerified === 1 ? (
                                    <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">Verified</span>
                                  ) : (
                                    <span className="bg-red-50 text-red-600 px-2 py-0.5 rounded-full">Not Verified</span>
                                  )}
                                  {codeVerifDate && (
                                    <div className="text-gray-400 mt-1">{formatDateTime(codeVerifDate)}</div>
                                  )}
                                </div>

                                {/* Status action buttons */}
                                {(isInProgress || isUnderVerif) && (
                                  <div className="flex gap-2 pt-2 border-t border-gray-100 flex-wrap">
                                    {isInProgress && (
                                      <button
                                        onClick={() => handleUpdateStatus(selectedDetail.id, "underVerification", p.sendParam)}
                                        className="flex-1 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                                      >
                                        Under Verification
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleUpdateStatus(selectedDetail.id, "rejected", p.sendParam)}
                                      className="flex-1 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition"
                                    >
                                      Reject
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400 italic">No {p.label} details available</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <FiShield size={28} className="text-gray-300 mb-2" />
                  <p className="text-sm">No details available.</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end rounded-b-xl flex-shrink-0">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
